<template>
	<div
		ref="scroller"
		class="toggle-chips"
		:class="[
			compact ? '[&_button]:!px-3 [&_button]:!py-[8px] [&_button]:!text-sm' : '',
			{ 'fade-left': fadeLeft, 'fade-right': fadeRight },
		]"
		@scroll="updateFades"
	>
		<div class="flex items-center w-max">
			<FormKit
				wrapper-class="$remove:mb-2"
				type="togglebuttons"
				:options="options"
				v-model="model"
			/>
		</div>
	</div>
</template>

<script setup lang="ts" generic="T extends string">
import { useUserStore } from "~stores/User";

const props = defineProps<{
	options: Record<T, string>;
}>();

const model = defineModel<T>({ required: true });

const userStore = useUserStore();
const compact = computed(() => userStore.activeUser?.settings?.touchScreen != true);

const scroller = ref<HTMLElement | null>(null);
const fadeLeft = ref(false);
const fadeRight = ref(false);

function updateFades() {
	const el = scroller.value;
	if (!el) return;
	fadeLeft.value = el.scrollLeft > 0;
	fadeRight.value = Math.ceil(el.scrollLeft + el.clientWidth) < el.scrollWidth;
}

let resizeObserver: ResizeObserver | undefined;
onMounted(() => {
	updateFades();
	resizeObserver = new ResizeObserver(() => updateFades());
	if (scroller.value) resizeObserver.observe(scroller.value);
});
onUnmounted(() => resizeObserver?.disconnect());

watch(
	() => props.options,
	() => nextTick(updateFades),
);
</script>

<style scoped>
.toggle-chips {
	display: flex;
	align-items: center;
	overflow-x: auto;
	overflow-y: hidden;
	scrollbar-width: thin;
	scrollbar-color: rgb(203 213 225) transparent; /* slate-300 thumb, invisible track */
	mask-repeat: no-repeat;
	mask-clip: content-box;
	mask-composite: intersect;
}
.toggle-chips::-webkit-scrollbar {
	height: 6px;
}
.toggle-chips::-webkit-scrollbar-thumb {
	background: rgb(203 213 225); /* slate-300 */
	border-radius: 9999px;
}
.toggle-chips::-webkit-scrollbar-track {
	background: transparent;
}
.toggle-chips.fade-left {
	mask-image: linear-gradient(to right, transparent 0%, black 8%, black 100%);
}
.toggle-chips.fade-right {
	mask-image: linear-gradient(to right, black 0%, black 92%, transparent 100%);
}
.toggle-chips.fade-left.fade-right {
	mask-image: linear-gradient(to right, transparent 0%, black 8%, black 92%, transparent 100%);
}

/* FormKit's genesis theme puts `mb-4` on .formkit-outer, which pushes the chips above the toolbar's
   centerline. Drop it so they center against sibling buttons in the flex row. */
.toggle-chips :deep(.formkit-outer) {
	margin-bottom: 0;
}

/* Keep every chip at its natural width: don't let the button group distribute/shrink the row (which
   squishes long labels), and don't wrap a label onto multiple lines. Overflow scrolls instead. */
.toggle-chips :deep(.formkit-options) {
	flex-wrap: nowrap;
}
.toggle-chips :deep(button) {
	flex-shrink: 0;
	white-space: nowrap;
}
</style>
